import React, { useState } from 'react';
import { Form, Input, Typography, notification, Row, Col } from 'antd';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import SEO from '../components/SEO';
import {
    MailOutlined, PhoneOutlined, EnvironmentOutlined,
    HeartOutlined, SendOutlined, ClockCircleOutlined,
} from '@ant-design/icons';
import LandingNavbar from '../layouts/Navbar';
import LandingFooter from '../layouts/Footer';

const { Title, Text } = Typography;
const { TextArea } = Input;

const ContactUs = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const { executeRecaptcha } = useGoogleReCaptcha();

    const onFinish = async (values) => {
        if (!executeRecaptcha) {
            notification.error({ message: 'reCAPTCHA not ready. Please try again.' });
            return;
        }
        setLoading(true);
        try {
            const recaptchaToken = await executeRecaptcha('contact_form');
            const response = await fetch(`${process.env.REACT_APP_API_URL}/enquiry`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...values, recaptcha_token: recaptchaToken }),
            });
            const data = await response.json();
            if (response.status === 201 && data.status === 'success') {
                notification.success({
                    message: 'Enquiry Sent Successfully',
                    description: 'We\'ll get back to you within 24 hours!',
                });
                form.resetFields();
            } else {
                notification.error({ message: 'Failed to Send', description: 'Something went wrong. Please try again.' });
            }
        } catch (error) {
            notification.error({ message: 'Failed to Send', description: 'An error occurred. Please try again.' });
        } finally {
            setLoading(false);
        }
    };

    const contactItems = [
        {
            icon: <MailOutlined />,
            label: 'Email Us',
            value: 'info[at]oottan[dot]com',
            sub: 'We reply within 24 hours',
            gradient: 'linear-gradient(135deg, #AD1457, #D81B60)',
        },
        {
            icon: <PhoneOutlined />,
            label: 'Call Us',
            value: '+91 7904428253',
            sub: 'Mon–Sat, 9 AM – 6 PM',
            gradient: 'linear-gradient(135deg, #880E6E, #AD1457)',
        },
        {
            icon: <ClockCircleOutlined />,
            label: 'Working Hours',
            value: 'Monday – Saturday',
            sub: '9:00 AM – 6:00 PM IST',
            gradient: 'linear-gradient(135deg, #F59E0B, #D97706)',
        },
        {
            icon: <EnvironmentOutlined />,
            label: 'Our Office',
            value: '8/61/1, Kumbavilai, Veeyanoor',
            sub: 'Kanyakumari District, Tamil Nadu – 629177',
            gradient: 'linear-gradient(135deg, #0F766E, #14B8A6)',
        },
    ];

    const inputStyle = { borderRadius: '10px', height: '48px', fontFamily: 'Calibri, sans-serif' };

    return (
        <div style={{ fontFamily: 'Calibri, sans-serif', minHeight: '100vh', background: '#FFFAFC' }}>
            <SEO
                title="Contact Us"
                description="Get in touch with Oottan's support team. We're here to help you with any questions about your matrimony journey."
                canonical="/contact-us"
            />
            <LandingNavbar />

            {/* ── Hero Banner ── */}
            <div style={{
                background: 'linear-gradient(135deg, #6A0572 0%, #880E6E 40%, #AD1457 80%, #D81B60 100%)',
                padding: '100px 24px 60px',
                textAlign: 'center',
                position: 'relative', overflow: 'hidden',
            }}>
                <div style={{
                    position: 'absolute', inset: 0,
                    backgroundImage: 'radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px)',
                    backgroundSize: '28px 28px',
                }} />
                <div style={{ position: 'relative', zIndex: 1 }}>
                    <Title style={{ color: 'white', fontSize: '2.4rem', fontWeight: '800', marginBottom: '12px', letterSpacing: '-0.5px' }}>
                        Get in Touch
                    </Title>
                    <Text style={{ color: 'rgba(255,255,255,0.78)', fontSize: '1rem' }}>
                        Have a question or feedback? We'd love to hear from you.
                    </Text>
                </div>
            </div>

            {/* ── Contact Cards ── */}
            <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 24px' }}>
                <Row gutter={[16, 16]} style={{ marginTop: '-32px', position: 'relative', zIndex: 10 }}>
                    {contactItems.map((item, i) => (
                        <Col key={i} xs={24} sm={12} md={6}>
                            <div style={{
                                background: 'white', borderRadius: '16px',
                                padding: '20px', textAlign: 'center',
                                boxShadow: '0 8px 32px rgba(173,20,87,0.1)',
                                border: '1px solid rgba(173,20,87,0.06)',
                                height: '100%',
                            }}>
                                <div style={{
                                    width: '48px', height: '48px', borderRadius: '12px',
                                    background: item.gradient,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    margin: '0 auto 12px',
                                    boxShadow: '0 4px 16px rgba(173,20,87,0.2)',
                                }}>
                                    {React.cloneElement(item.icon, { style: { color: 'white', fontSize: '1.2rem' } })}
                                </div>
                                <Text style={{ color: '#AD1457', fontWeight: '700', fontSize: '0.78rem', letterSpacing: '0.5px', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>
                                    {item.label}
                                </Text>
                                <Text style={{ color: '#1A1A2E', fontWeight: '600', fontSize: '0.9rem', display: 'block', marginBottom: '4px' }}>
                                    {item.value}
                                </Text>
                                <Text style={{ color: '#94A3B8', fontSize: '0.8rem' }}>
                                    {item.sub}
                                </Text>
                            </div>
                        </Col>
                    ))}
                </Row>
            </div>

            {/* ── Form + Map Section ── */}
            <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '48px 24px 80px' }}>
                <Row gutter={[40, 40]}>

                    {/* Form */}
                    <Col xs={24} md={14}>
                        <div style={{
                            background: 'white', borderRadius: '20px',
                            padding: '40px', border: '1px solid rgba(173,20,87,0.08)',
                            boxShadow: '0 8px 40px rgba(173,20,87,0.08)',
                        }}>
                            <div style={{ marginBottom: '28px' }}>
                                <div style={{
                                    width: '40px', height: '4px', borderRadius: '9999px',
                                    background: 'linear-gradient(135deg, #AD1457, #D81B60)',
                                    marginBottom: '14px',
                                }} />
                                <Title level={3} style={{ color: '#1A1A2E', marginBottom: '4px', fontWeight: '800' }}>
                                    Send Us a Message
                                </Title>
                                <Text style={{ color: '#64748B', fontSize: '0.92rem' }}>
                                    Fill in the form and we'll get back to you soon.
                                </Text>
                            </div>

                            <Form form={form} name="contact" onFinish={onFinish} layout="vertical" requiredMark={false}>
                                <Row gutter={16}>
                                    <Col xs={24} sm={12}>
                                        <Form.Item name="name" label={<Text style={{ fontWeight: '600', color: '#374151' }}>Full Name</Text>} rules={[{ required: true, message: 'Required' }]}>
                                            <Input prefix={<HeartOutlined style={{ color: '#AD1457' }} />} placeholder="Your full name" style={inputStyle} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <Form.Item name="phone" label={<Text style={{ fontWeight: '600', color: '#374151' }}>Phone Number</Text>} rules={[{ required: true, message: 'Required' }]}>
                                            <Input prefix={<PhoneOutlined style={{ color: '#AD1457' }} />} placeholder="Your phone number" style={inputStyle} />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Form.Item name="email" label={<Text style={{ fontWeight: '600', color: '#374151' }}>Email Address</Text>} rules={[{ required: true, message: 'Required' }, { type: 'email', message: 'Invalid email' }]}>
                                    <Input prefix={<MailOutlined style={{ color: '#AD1457' }} />} placeholder="Your email address" style={inputStyle} />
                                </Form.Item>

                                <Form.Item name="subject" label={<Text style={{ fontWeight: '600', color: '#374151' }}>Subject</Text>} rules={[{ required: true, message: 'Required' }]}>
                                    <Input placeholder="What is this about?" style={inputStyle} />
                                </Form.Item>

                                <Form.Item name="message" label={<Text style={{ fontWeight: '600', color: '#374151' }}>Message</Text>} rules={[{ required: true, message: 'Required' }]}>
                                    <TextArea placeholder="Write your message here..." rows={5} style={{ borderRadius: '10px', resize: 'none', fontFamily: 'Calibri, sans-serif' }} />
                                </Form.Item>

                                <Form.Item style={{ marginBottom: 0 }}>
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        style={{
                                            width: '100%', height: '50px', borderRadius: '10px',
                                            background: loading ? '#ccc' : 'linear-gradient(135deg, #AD1457, #D81B60)',
                                            color: 'white', border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
                                            fontSize: '1rem', fontWeight: '700', fontFamily: 'Calibri, sans-serif',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                                            boxShadow: '0 6px 20px rgba(173,20,87,0.3)',
                                            transition: 'all 0.25s ease',
                                        }}
                                        onMouseEnter={(e) => { if (!loading) e.currentTarget.style.boxShadow = '0 8px 28px rgba(173,20,87,0.45)'; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 6px 20px rgba(173,20,87,0.3)'; }}
                                    >
                                        <SendOutlined /> {loading ? 'Sending...' : 'Send Message'}
                                    </button>
                                </Form.Item>
                            </Form>
                        </div>
                    </Col>

                    {/* Right — Address detail + office info */}
                    <Col xs={24} md={10}>
                        <div style={{
                            background: 'linear-gradient(135deg, #880E6E 0%, #AD1457 60%, #D81B60 100%)',
                            borderRadius: '20px', padding: '40px',
                            boxShadow: '0 8px 40px rgba(173,20,87,0.25)',
                            height: '100%', position: 'relative', overflow: 'hidden',
                        }}>
                            <div style={{
                                position: 'absolute', top: '-60px', right: '-60px',
                                width: '200px', height: '200px', borderRadius: '50%',
                                background: 'rgba(255,255,255,0.06)',
                            }} />
                            <div style={{
                                position: 'absolute', bottom: '-40px', left: '-40px',
                                width: '150px', height: '150px', borderRadius: '50%',
                                background: 'rgba(255,255,255,0.04)',
                            }} />

                            <div style={{ position: 'relative', zIndex: 1 }}>
                                <div style={{
                                    width: '40px', height: '4px', borderRadius: '9999px',
                                    background: 'rgba(255,255,255,0.5)', marginBottom: '20px',
                                }} />
                                <Title level={3} style={{ color: 'white', fontWeight: '800', marginBottom: '8px' }}>
                                    Visit Our Office
                                </Title>
                                <Text style={{ color: 'rgba(255,255,255,0.72)', fontSize: '0.92rem', display: 'block', marginBottom: '32px' }}>
                                    We'd love to meet you in person.
                                </Text>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                                    {[
                                        { icon: <EnvironmentOutlined />, label: 'Address', value: 'Hirehike Technologies Private Limited\n8/61/1, Kumbavilai, Chaicode\nVeeyanoor, Kanyakumari District\nTamil Nadu, India – 629177' },
                                        { icon: <MailOutlined />, label: 'Email', value: 'info[at]oottan[dot]com' },
                                        { icon: <PhoneOutlined />, label: 'Phone', value: '+91 7904428253' },
                                        { icon: <ClockCircleOutlined />, label: 'Hours', value: 'Mon–Sat: 9 AM – 6 PM IST' },
                                    ].map((item, i) => (
                                        <div key={i} style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                                            <div style={{
                                                width: '36px', height: '36px', borderRadius: '10px', flexShrink: 0,
                                                background: 'rgba(255,255,255,0.15)',
                                                border: '1px solid rgba(255,255,255,0.2)',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            }}>
                                                {React.cloneElement(item.icon, { style: { color: 'white', fontSize: '1rem' } })}
                                            </div>
                                            <div>
                                                <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.72rem', fontWeight: '600', letterSpacing: '0.5px', textTransform: 'uppercase', display: 'block', marginBottom: '3px' }}>
                                                    {item.label}
                                                </Text>
                                                <Text style={{ color: 'white', fontSize: '0.9rem', whiteSpace: 'pre-line', lineHeight: '1.6' }}>
                                                    {item.value}
                                                </Text>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </Col>
                </Row>
            </div>

            <LandingFooter />
        </div>
    );
};

export default ContactUs;
